import { inject, Injectable } from '@angular/core';
import { MissionsApiService } from './api/missions-api.service';
import { DBService } from './db.service';
import { FileService } from './file.service';
import { HttpErrorResponse } from '@angular/common/http';
import { liveQuery } from 'dexie';
import { appDb } from 'dexie/app.db';
import { generateUUID } from 'helpers/string.helper';
import { from, of, switchMap, catchError, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MissionsService {
  protected dbService = inject(DBService);
  protected fileService = inject(FileService);
  protected missionsApiService = inject(MissionsApiService);
  constructor() {}
  getMissions(filtersAndSorts?: any) {
    let dbQuery: any = appDb.missions.where('isDeleted').notEqual(1);
    if (filtersAndSorts?.title) {
      dbQuery = dbQuery.and((item) =>
        (item.title || '')
          .toLowerCase()
          .includes(filtersAndSorts.title.toLowerCase())
      );
    }
    if (filtersAndSorts?.content) {
      dbQuery = dbQuery.and((item) =>
        item.content
          .toLowerCase()
          .includes(filtersAndSorts.content.toLowerCase())
      );
    }
    if (filtersAndSorts?.deadlineStart || filtersAndSorts?.deadlineEnd) {
      dbQuery = dbQuery.and((item) => {
        const deadline = new Date(item.deadline);
        return (
          deadline >= new Date(filtersAndSorts?.deadlineStart || 0) &&
          deadline <=
            new Date(filtersAndSorts?.deadlineEnd || Number.MAX_SAFE_INTEGER)
        );
      });
    }
    if (filtersAndSorts?.userId?.length) {
      dbQuery = dbQuery.and((item) =>
        (Array.isArray(filtersAndSorts.userId)
          ? filtersAndSorts.userId
          : [filtersAndSorts.userId]
        ).some((userId) => userId === item.userId)
      );
    }
    if (filtersAndSorts?.priority !== undefined) {
      dbQuery = dbQuery.and(
        (item) => item.priority === Number(filtersAndSorts.priority)
      );
    }
    return from(
      liveQuery(() => {
        if (filtersAndSorts.sortBy && filtersAndSorts.sortDir) {
          if (filtersAndSorts.sortDir === 'desc') {
            return dbQuery.sortBy(filtersAndSorts.sortBy);
          } else {
            return dbQuery.reverse().sortBy(filtersAndSorts.sortBy);
          }
        } else {
          return dbQuery.toArray();
        }
      })
    );
  }

  createMission(mission) {
    return this.missionsApiService.create({ ...mission }).pipe(
      catchError((err: HttpErrorResponse) => {
        if (!navigator.onLine) {
          return this.dbService.createRow(appDb.missions, {
            ...mission,
            isSynced: 0,
            isDeleted: 0,
          });
        }
        return throwError(() => err);
      })
    );
  }

  updateMission(id, mission) {
    if (mission && 'isDeleted' in mission) {
      delete mission.isDeleted;
    }
    return from(appDb.missions.get(id)).pipe(
      switchMap((res) =>
        this.missionsApiService
          .update(res.id, {
            ...mission,
          })
          .pipe(
            catchError((err: HttpErrorResponse) => {
              if (!navigator.onLine) {
                return this.dbService.updateRow(appDb.missions, {
                  ...mission,
                  isSynced: 0,
                });
              }
              return throwError(() => err);
            })
          )
      )
    );
  }

  delete(id) {
    return this.missionsApiService.delete(id).pipe(
      switchMap((res: any) => this.dbService.hardDeleteRow(appDb.missions, id)),
      catchError((err: HttpErrorResponse) => {
        if (!navigator.onLine) {
          return this.dbService.updateRow(appDb.missions, {
            id,
            isDeleted: 1,
            isSynced: 0,
          });
        }
        return throwError(() => err);
      })
    );
  }

  getNotSyncedItems() {
    return from(appDb.missions.where('isSynced').equals(0).toArray());
  }

  getNotSyncedItemsCount() {
    return from(
      liveQuery(() => appDb.missions.where('isSynced').equals(0).count())
    );
  }
  bulkUpdateLocal(items) {
    return from(appDb.missions.bulkPut(items));
  }
}
